import React, { useState } from "react";
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Button,
    Grid,
    IconButton,
    TextField,
    makeStyles,
    createStyles,
    Theme
} from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { Edit, Delete, Add, Close, Check } from "@material-ui/icons";
import CustomCardTitle from "../../../../interfaces/common/card/CustomCardTitle";
import { IStudentPaymentPlanSchedule } from "../../../../interfaces/student/financials/IStudentPaymentPlan";
import { EmptyGuid } from "../../../../utils/constants";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // ...other styles...
        noNumberArrows: {
            // Chrome, Safari, Edge, Opera
            "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
            },
            // Firefox
            "& input[type=number]": {
                MozAppearance: "textfield",
            },
        },
    })
);



interface StudentPaymentPlanTableProps {
    planSchedule: IStudentPaymentPlanSchedule[];
    paymentPlanId: string;
    onExportPDF?: () => void;
    onUpdate?: (newSchedule: IStudentPaymentPlanSchedule[]) => void;
    onDelete?: (newSchedule: IStudentPaymentPlanSchedule[]) => void;
}

const StudentPaymentPlanTable: React.FC<StudentPaymentPlanTableProps> = ({
    planSchedule,
    paymentPlanId,
    onExportPDF,
    onUpdate,
    onDelete
}) => {
    const emptyRow: IStudentPaymentPlanSchedule = {
        scheduleId: "",
        paymentPlanId: paymentPlanId,
        expectedDate: null,
        amount: 0,
        balance: 0,
        receivedAmount: 0,
        reference: "",
        modifiedUser: "",
        modifiedDate: "",
        transactionId: null,
        receivedDate: null
    };

    const classes = useStyles({});

    const [rows, setRows] = useState<IStudentPaymentPlanSchedule[]>(planSchedule || []);
    const [editIdx, setEditIdx] = useState<string | null>(null);
    const [editRow, setEditRow] = useState<IStudentPaymentPlanSchedule | null>(null);
    const [addRow, setAddRow] = useState<IStudentPaymentPlanSchedule>({ ...emptyRow });

    React.useEffect(() => {
        setRows(planSchedule || []);
    }, [planSchedule]);

    React.useEffect(() => {
        setAddRow({ ...emptyRow, paymentPlanId });
    }, [paymentPlanId]);

    const handleEdit = (scheduleId: string) => {
        const rowToEdit = rows.find(r => r.scheduleId === scheduleId);
        setEditIdx(scheduleId);
        setEditRow(rowToEdit ? { ...rowToEdit } : null);
    };

    const handleEditChange = (field: keyof IStudentPaymentPlanSchedule, value: any) => {
        if (!editRow) return;
        setEditRow({ ...editRow, [field]: value });
    };

    const handleEditSave = () => {
        if (!editIdx || !editRow) return;
        const updated = rows.map(r =>
            r.scheduleId === editIdx ? { ...editRow, paymentPlanId } : r
        );
        setRows(updated);
        setEditIdx(null);
        setEditRow(null);
        onUpdate && onUpdate(updated);
    };

    const handleEditCancel = () => {
        setEditIdx(null);
        setEditRow(null);
    };

    const handleDelete = (scheduleId: string) => {
        const updated = rows.filter(r => r.scheduleId !== scheduleId);
        setRows(updated);
        onDelete && onDelete(updated);
    };

    const handleAddChange = (field: keyof IStudentPaymentPlanSchedule, value: any) => {
        setAddRow({ ...addRow, [field]: value });
    };

    const handleAdd = () => {
        if (!addRow.expectedDate || !addRow.amount) return;
        const newRow = {
            ...addRow,
            scheduleId: EmptyGuid,
            paymentPlanId
        };
        const updated = [...rows, newRow];
        setRows(updated);
        setAddRow({ ...emptyRow, paymentPlanId });
        onUpdate && onUpdate(updated);
    };

    const sortedRows = rows
        .slice()
        .sort((a, b) => {
            const dateA = a.expectedDate ? new Date(a.expectedDate).getTime() : 0;
            const dateB = b.expectedDate ? new Date(b.expectedDate).getTime() : 0;
            return dateA - dateB;
        });

    const isClosedOut = rows.length > 0 && rows.every(r => Number(r.balance) === 0);

    return (
        <>
            <CustomCardTitle title="Student Payment Plan" />
            <Grid container alignItems="center" style={{ marginBottom: 8 }}>
                <Grid item>
                    <Button
                        variant="contained"
                        color="default"
                        startIcon={<span className="fa fa-file-pdf-o" />}
                        onClick={onExportPDF}
                    >
                        EXPORT TO PDF
                    </Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper} style={{ boxShadow: "none" }} className={classes.noNumberArrows}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>Scheduled Date</TableCell>
                            <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>Reference</TableCell>
                            <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>Projected Amount</TableCell>
                            <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>Received Amount</TableCell>
                            <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>Balance</TableCell>
                            <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>Received Date</TableCell>
                            <TableCell style={{ padding: "4px 8px", width: 60, textAlign: "center" }}></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sortedRows.map((row, idx) =>
                            editIdx === row.scheduleId ? (
                                <TableRow key={row.scheduleId || idx}>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <KeyboardDatePicker
                                                disableToolbar
                                                variant="inline"
                                                format="MM/dd/yyyy"
                                                autoOk={true}
                                                required={true}
                                                value={editRow?.expectedDate || null}
                                                onChange={date => handleEditChange("expectedDate", date)}
                                                inputVariant="standard"
                                                style={{ width: 135 }}
                                                disabled={!!editRow?.receivedAmount && Number(editRow.receivedAmount) !== 0}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        <TextField
                                            value={editRow?.reference || ""}
                                            onChange={e => handleEditChange("reference", e.target.value)}
                                            style={{ width: 90 }}
                                        />
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        <TextField
                                            type="number"
                                            value={editRow?.amount ?? ""}
                                            onChange={e => {
                                                const val = Number(e.target.value);
                                                handleEditChange("amount", isNaN(val) ? 0 : val);
                                            }}
                                            style={{ width: 90 }}
                                            disabled={!!editRow?.receivedAmount && Number(editRow.receivedAmount) !== 0}
                                            inputProps={{ step: "0.01" }}
                                        />
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        <TextField
                                            value={editRow?.receivedAmount ?? ""}
                                            onChange={e => {
                                                const val = Number(e.target.value);
                                                handleEditChange("receivedAmount", isNaN(val) ? 0 : val);
                                            }}
                                            style={{ width: 90 }}
                                            disabled
                                        />
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        <TextField
                                            value={editRow?.balance ?? ""}
                                            onChange={e => {
                                                const val = Number(e.target.value);
                                                handleEditChange("balance", isNaN(val) ? 0 : val);
                                            }}
                                            style={{ width: 90 }}
                                            disabled
                                        />
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <KeyboardDatePicker
                                                disableToolbar
                                                variant="inline"
                                                format="MM/dd/yyyy"
                                                autoOk={true}
                                                required={true}
                                                value={editRow?.modifiedDate ? new Date(editRow.modifiedDate) : null}
                                                onChange={date => handleEditChange("receivedDate", date)}
                                                inputVariant="standard"
                                                style={{ width: 135 }}
                                                disabled
                                            />
                                        </MuiPickersUtilsProvider>
                                    </TableCell>
                                    <TableCell style={{ padding: "4px 8px", width: 60, textAlign: "center" }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                            <IconButton onClick={handleEditSave} color="primary" size="small">
                                                <Check fontSize="small" />
                                            </IconButton>
                                            <IconButton onClick={handleEditCancel} color="inherit" size="small">
                                                <Close fontSize="small" style={{ color: "#000" }} />
                                            </IconButton>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <TableRow key={row.scheduleId || idx}>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        {row.expectedDate
                                            ? new Date(row.expectedDate).toLocaleDateString("en-US").replace(/^0+/, '').replace(/\/0+/g, '/')
                                            : ""}
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>{row.reference}</TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        {row.amount?.toLocaleString("en-US", {
                                            style: "currency",
                                            currency: "USD"
                                        })}
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        {row.receivedAmount?.toLocaleString("en-US", {
                                            style: "currency",
                                            currency: "USD"
                                        })}
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        {row.balance?.toLocaleString("en-US", {
                                            style: "currency",
                                            currency: "USD"
                                        })}
                                    </TableCell>
                                    <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                        {row.modifiedDate ? (() => {
                                            const isoMatch = row.modifiedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
                                            let dateObj: Date;

                                            if (isoMatch) {
                                                const [, year, month, day] = isoMatch;
                                                dateObj = new Date(+year, +month - 1, +day);
                                            } else {
                                                const [month, day, year] = row.modifiedDate.split('/');
                                                dateObj = new Date(+year, +month - 1, +day);
                                            }

                                            if (isNaN(dateObj.getTime())) {
                                                return row.modifiedDate;
                                            }

                                            return dateObj.toLocaleDateString('en-US', {
                                                month: 'numeric',
                                                day: 'numeric',
                                                year: 'numeric'
                                            });
                                        })() : ""}

                                    </TableCell>
                                    <TableCell style={{ padding: "4px 8px", width: 60, textAlign: "center" }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                            {(!row.receivedAmount || Number(row.receivedAmount) === 0) && (
                                                <>
                                                    <IconButton onClick={() => handleEdit(row.scheduleId)} color="primary" size="small">
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDelete(row.scheduleId)} color="inherit" size="small">
                                                        <Delete fontSize="small" style={{ color: "#000" }} />
                                                    </IconButton>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                        {!isClosedOut && (
                            <TableRow>
                                <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                        <KeyboardDatePicker
                                            disableToolbar
                                            variant="inline"
                                            format="MM/dd/yyyy"
                                            autoOk={true}
                                            required={true}
                                            value={addRow.expectedDate || null}
                                            onChange={date => handleAddChange("expectedDate", date)}
                                            inputVariant="standard"
                                            placeholder="MM/DD/YYYY"
                                            style={{ width: 135 }}
                                        />
                                    </MuiPickersUtilsProvider>
                                </TableCell>
                                <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                    <TextField
                                        value={addRow.reference || ""}
                                        onChange={e => handleAddChange("reference", e.target.value)}
                                        style={{ width: 90 }}
                                        disabled
                                    />
                                </TableCell>
                                <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                    <TextField
                                        type="number"
                                        value={addRow.amount ?? ""}
                                        onChange={e => {
                                            const val = Number(e.target.value);
                                            handleAddChange("amount", isNaN(val) ? 0 : val);
                                        }}
                                        style={{ width: 90 }}
                                        inputProps={{ step: "0.01" }}
                                    />
                                </TableCell>
                                <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                    <TextField
                                        value={addRow.receivedAmount ?? ""}
                                        onChange={e => {
                                            const val = Number(e.target.value);
                                            handleAddChange("receivedAmount", isNaN(val) ? 0 : val);
                                        }}
                                        style={{ width: 90 }}
                                        disabled
                                    />
                                </TableCell>
                                <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                    <TextField
                                        value={addRow.balance ?? ""}
                                        onChange={e => {
                                            const val = Number(e.target.value);
                                            handleAddChange("balance", isNaN(val) ? 0 : val);
                                        }}
                                        style={{ width: 90 }}
                                        disabled
                                    />
                                </TableCell>
                                <TableCell style={{ padding: "8px 12px", fontSize: 15 }}>
                                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                        <KeyboardDatePicker
                                            disableToolbar
                                            variant="inline"
                                            format="MM/dd/yyyy"
                                            autoOk={true}
                                            required={true}
                                            value={addRow.receivedDate ? new Date(addRow.receivedDate) : null}
                                            onChange={date => handleAddChange("receivedDate", date)}
                                            inputVariant="standard"
                                            placeholder="MM/DD/YYYY"
                                            style={{ width: 135 }}
                                            disabled
                                        />
                                    </MuiPickersUtilsProvider>
                                </TableCell>
                                <TableCell style={{ padding: "4px 8px", width: 60, textAlign: "center" }}>
                                    <IconButton onClick={handleAdd} color="primary" size="small">
                                        <Add fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default StudentPaymentPlanTable;